import { useState } from 'react';
import TitleBar from '../../../../components/TitleBar';
import { ColumnsType } from 'antd/es/table';
import { Footer } from '../../../../components/common/Footer';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../components';
import { FETCH_DIVISION_SELECTION } from '../../queries';
import { useLazyQuery, useQuery } from '@apollo/client';
import { FindAllLocations } from '../../../dataInputs/locations/Locations';
import TableV3 from '../../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../../utils';
import QueryErrorBoundary from '../../../../components/error-handling';
import { baseColumns } from './exportColumns';
import { useUserContext } from '../../../../context/PermissionsContext';
import { getFilteredTags } from '../../../../utils/getFilteredTags';
import { REFRESH_DEPENDENCIES } from '../../queries/DivisionSelection';
import { message } from 'antd';
import TitleUpdater from '../../../../components/common/TitleUpdater';

type DivisionDashBoard = {
  fGLocations: number;
  l1Locations: number;
  l2Locations: number;
  l3PlusLocations: number;
  locations: FindAllLocations[];
  name: string;
  percentage: number;
  uuid: string;
};

interface DataReturned {
  divisionDashBoard: DivisionDashBoard[];
}

function DivisionSelection() {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_DIVISION_SELECTION,
  );

  const { selectedTags, isDivision } = useUserContext();

  const divisionsData = data?.divisionDashBoard || [];

  const filteredDivisions = getFilteredTags({
    data: divisionsData,
    selectedTags,
    condition: isDivision,
    lookUpKey: 'uuid',
  });

  const [locationsSelected] = useState<string[]>([]);

  const [refreshDependencies, { loading: refreshedLoading }] =
    useLazyQuery(REFRESH_DEPENDENCIES);

  const handleClick = (record: DivisionDashBoard) => {
    record.locations?.map((location) => {
      locationsSelected.push(location.uuid);
    });
    let params = locationsSelected.map((value) => `${value}`).join(',');
    localStorage.setItem('locations', params);
    navigate(
      `${record.uuid}/finished-goods-locations?divisionName=${record.name}`,
    );
  };

  const handleRefresh = async () => {
    try {
      const { data } = await refreshDependencies();

      if (data?.updateMissingDependencies) {
        message.success('Dependencies refreshed successfully');
      } else {
        message.error('No dependencies were updated');
      }
    } catch (error) {
      message.error('Failed to refresh dependencies');
      console.error(error);
    }
  };

  const expandActions = (record: any) => {
    return (
      <div className="w-full flex items-center justify-center gap-3">
        <Button
          key={record.uuid}
          type="primary-rounded"
          className="text-sm min-w-[180px]"
          onClick={() => navigate(`${record.uuid}/dependencies-tree`)}
        >
          Dependencies Tree
        </Button>
        <Button
          key={record.uuid}
          type="primary-rounded"
          className="text-sm min-w-[180px]"
          onClick={() => handleClick(record)}
        >
          Map Dependencies
        </Button>
      </div>
    );
  };

  const columns: ColumnsType<DivisionDashBoard> = [
    {
      title: 'Division',
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      width: 150,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: '% of Finished Goods Mapped',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'right',
      width: 100,
      sorter: (a, b) => tableSortingMethods.number(a.percentage, b.percentage),
      render: (text: string) => (
        <span className="custom-table--column">{text} %</span>
      ),
    },
    {
      title: 'FG Locations Mapped',
      dataIndex: 'fGLocations',
      key: 'fGLocations',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.fGLocations, b.fGLocations),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'L1 Locations Mapped',
      dataIndex: 'l1Locations',
      key: 'l1Locations',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.l1Locations, b.l1Locations),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'L2 Locations Mapped',
      dataIndex: 'l2Locations',
      key: 'l2Locations',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.l2Locations, b.l2Locations),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'L3+ Locations Mapped',
      dataIndex: 'l3PlusLocations',
      key: 'l3PlusLocations',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.l3PlusLocations, b.l3PlusLocations),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
  ];

  return (
    <>
      <TitleUpdater title={`Location Dependency Mapping`} />
      <TitleBar
        title="Dependency Mapping"
        brief="Mapping dependencies is a daunting process, but MEO makes it easy. Start by mapping
                      locations that produce finished goods. Later, MEO walks through how to capture interdependencies
                      further up the company's value / supply chain."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Divisions"
          columns={columns}
          data={filteredDivisions}
          loading={loading || refreshedLoading}
          extendActions={expandActions}
          exportExcelColumns={baseColumns}
          onRefresh={handleRefresh}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/exposures'} />
    </>
  );
}

export default DivisionSelection;
